<template>
  <panel-layout
    id="export-panel"
    :heading="$t('pages.export.index.heading')"
    :info="$t('pages.export.index.info')"
    :loading="loading"
  >
    <div class="h-full w-full">
      <o-scroll class="h-full p-4">
        <o-field-group :label="$t('pages.export.index.type')" class="block mb-3 pb-3 border-b">
          <select-field v-model="exporter" :options="options" />
        </o-field-group>

        <div v-if="exporter && params">
          <o-field-group v-for="param in params" :key="param.key" :label="param.label" :rules="param.required ? 'required' : ''" class="block mb-4">
            <template v-if="param.component">
              <component :is="param.component" :model-value="param.value" @update:model-value="(val: any) => handleParamUpdate(param, val)" :options="param.values" :filter-preset="param.filters" class="w-full" />
            </template>

            <template v-else>
              {{ param.modelType }} - {{ param.type }}
            </template>
          </o-field-group>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div>
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="success" icon="export" class="mr-2" :disabled="!valid" @click="confirm()">
              {{ $t('pages.export.index.create') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import Fields from '@/components/export/elements/fields'

import { SelectField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = withDefaults(defineProps<{
  presets?: Record<string, any>
}>(), {
  presets: () => ({}),
})

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const exporter = ref<any>(null)
const exporters = ref<any>({})
const params = shallowRef<any[]>([])
const loading = ref(false)


const options = computed(() => {
  return Object.entries(exporters.value)
    .map(([key, value]: [string, any]) => {
      return {
        label: value.exporterLabel,
        value,
      }
    })
})

const handleParamUpdate = (param: any, val: any) => {
  param.value = val
  triggerRef(params)
}

const valid = computed(() => {
  for (const param of params.value) {
    if (param.required) {
      let value = param.value
      if (value && param.validate) {
        value = param.validate(value)
      }
      if (!value) {
        return false
      }
    }
  }
  return true
})

watch(exporter, () => {
  const exp = exporter.value
  const presets = props.presets

  if (exp?.params) {
    params.value = Object.entries(exp.params)
      .map(([key, param]: [string, any]) => {
        const { type, modelType, label, values, required } = param
        const field = Fields[type]?.[modelType] || Fields[type]

        const value = presets?.params?.[key] || Fields[type]?.[key]?.default || field?.default || null

        return {
          key,
          value,
          label,
          type,
          values: values?.map((v: any) => { return { label: v, value: v } }),
          filters: presets?.filters?.[key],
          required,
          modelType,
          component: field?.component ? markRaw(field.component) : null,
          reduce: field?.reduce || null,
        }
      })
  }
})

const fetchExporters = async () => {
  loading.value = true

  try {
    const response = await $api.export.exporters()
    exporters.value = response.data
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const confirm = async () => {
  loading.value = true

  try {
    const exp = exporter.value
    const type = exp.type

    const paramsObj = params.value
      .reduce((acc: any, param: any) => {
        let value = param.value
        if (param.reduce) {
          value = param.reduce(value)
        }
        acc[param.key] = value
        return acc
      }, {})

    const response = await $api.export.exportData({ type, params: paramsObj })

    if (response) {
      $notification.toast({
        title: $i18n.t('export.requested'),
        text: $i18n.t('pages.export.index.successInfo'),
        variant: 'success',
        duration: 10000,
      })

      $bus.emit.created('Export')
      panel.close()
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const cancel = () => {
  panel.close()
}

// created
;(async () => {
  await fetchExporters()
  const presets = props.presets
  exporter.value = presets?.type ? exporters.value[presets.type] : null
})()
</script>
